import styled from '@emotion/styled';
import { alpha, Typography } from '@mui/material';

import { useAppSelector } from '@/redux/hooks';
import { TitleEntityV2 } from '@/redux/slices/titleV2Slice.types';
import { TitleEntity, VersionGroupedByParent } from '@/redux/slices/slice_types';
import { selectVersionKeys } from '@/redux/selectors/orderPlacementSelectors';
import { capitalizeNoun } from '@/_utils/utils';
import { TitleVersionChip } from '@/exports/columnCommonComponents';

type ScheduleTitleCardProps = {
  title: TitleEntity | TitleEntityV2;
  titleGroup: VersionGroupedByParent;
};

const ScheduleTitleCard = ({ title, titleGroup }: ScheduleTitleCardProps) => {
  const versionsByKey = useAppSelector(selectVersionKeys);
  const selectedCount = Object.keys(titleGroup).length;
  const sequenceNumber = Math.trunc(+title.sequence_order);

  return (
    <TitleCard>
      <TitleHeader>
        <Typography>{capitalizeNoun(title.entity_type)}</Typography>
        <Typography>
          {selectedCount} Title{selectedCount > 1 ? 's' : ''} selected
        </Typography>
      </TitleHeader>
      <TitleContent>
        <TitleDetails>
          <Typography>E{sequenceNumber}:</Typography>
          <Typography>{title.name}</Typography>
        </TitleDetails>
        <TitleVersionsContainer>
          {Object.keys(titleGroup).map((versionKey) => {
            const version = versionsByKey[versionKey.substring(1)];
            return (
              <TitleVersion key={versionKey}>
                <Typography>{version.alpha_id}</Typography>
                <TitleVersionChip withTooltip version={version} size="small" />
              </TitleVersion>
            );
          })}
        </TitleVersionsContainer>
      </TitleContent>
    </TitleCard>
  );
};

export default ScheduleTitleCard;

const TitleCard = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(2),
  border: `1px solid ${alpha('#4C4E5CB2', 0.7)}`,
  borderRadius: 8,
  backgroundColor: theme.palette.background.paper,
  gap: theme.spacing(1),
  boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.25)'
}));

const TitleHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2),
  '& .MuiTypography-root:first-of-type': {
    fontWeight: 500,
    color: '#D9D9D9'
  },
  '& .MuiTypography-root:last-child': {
    fontSize: theme.typography.pxToRem(14),
    color: alpha(theme.palette.text.primary, 0.5)
  }
}));

const TitleContent = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  alignItems: 'start'
}));

const TitleDetails = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.5),
  flex: '1',
  '& .MuiTypography-root:first-of-type': {
    color: alpha(theme.palette.text.primary, 0.5)
  },
  '& .MuiTypography-root:last-child': {
    color: '#D9D9D9',
    fontWeight: 500
  }
}));

const TitleVersionsContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1.5),
  flexBasis: 200
}));

const TitleVersion = styled('div')(({ theme }) => ({
  display: 'flex',
  gap: theme.spacing(1),
  '& > .MuiTypography-root': {
    color: '#D9D9D9'
  },
  '& > div': {
    marginTop: theme.spacing(-0.75),
    marginLeft: 'auto'
  }
}));
